import { useDeferredValue, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { workersApi, type WorkerCreationPost } from '@/services/workers'
import { categoriesApi } from '@/services/categories'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch, getServiceInstanceIdFromToken, withServiceInstanceId } from '@/lib/service-instance'
import { useAuthStore } from '@/stores/auth-store'

const workerSchema = z.object({
  post_id: z.coerce.string().trim().min(1, 'Post holder is required'),
  display_name: z.string().trim().min(1, 'Display name is required'),
  phone: z.string().trim().optional(),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  worker_type: z.coerce.number().int().default(1),
  verified: z.boolean().default(false),
  categories: z.array(z.string()).optional(),
})

type WorkerFormData = z.infer<typeof workerSchema>

const normalizeCategoryId = (categoryId: string | number) => String(categoryId)

const toggleCategory = (
  current: string[] | undefined,
  categoryId: string,
  checked: boolean,
) => {
  const normalizedId = normalizeCategoryId(categoryId)
  const values = (current ?? []).map(normalizeCategoryId)
  if (checked) {
    return values.includes(normalizedId) ? values : [...values, normalizedId]
  }
  return values.filter((value) => value !== normalizedId)
}

const getPostSecondaryText = (post: WorkerCreationPost) => {
  return [post.username, post.service_instance_name]
    .filter(Boolean)
    .join(' • ')
}

export default function WorkerCreatePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((state) => state.accessToken)
  const currentPost = useAuthStore((state) => state.currentPost)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPostServiceInstanceId = String(
    currentPost?.service_instance
    ?? currentPost?.service_instance_id
    ?? '',
  ).trim() || null
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
    ?? currentPostServiceInstanceId
    ?? storedServiceInstanceId
    ?? getServiceInstanceIdFromToken(accessToken)
  const [postPickerOpen, setPostPickerOpen] = useState(false)
  const [postSearch, setPostSearch] = useState('')
  const [selectedPost, setSelectedPost] = useState<WorkerCreationPost | null>(null)
  const [verifiedConfirmOpen, setVerifiedConfirmOpen] = useState(false)
  const deferredPostSearch = useDeferredValue(postSearch.trim().toLowerCase())

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<WorkerFormData>({
    resolver: zodResolver(workerSchema),
    defaultValues: {
      worker_type: 1,
      verified: false,
      categories: [],
    },
  })

  const postsQuery = useQuery({
    queryKey: ['worker-creation-posts', serviceInstanceId],
    queryFn: () => workersApi.getWorkerCreationPosts({
      serviceInstanceId: serviceInstanceId!,
      eligibleOnly: false,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const categoriesQuery = useQuery({
    queryKey: ['job-categories', 'worker-create', serviceInstanceId ?? 'none'],
    queryFn: () => categoriesApi.getCategories(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const categories = categoriesQuery.data?.results ?? []

  const filteredPosts = (postsQuery.data ?? []).filter((post) => {
    if (post.has_worker_profile) {
      return false
    }
    if (!deferredPostSearch) {
      return true
    }
    const haystack = [post.user_name, post.username, post.role_name, post.service_instance_name]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(deferredPostSearch)
  })

  const createMutation = useMutation({
    mutationFn: workersApi.createWorker,
    onSuccess: (worker) => {
      toast.success('Worker created successfully')
      queryClient.invalidateQueries({ queryKey: ['workers'] })
      queryClient.invalidateQueries({ queryKey: ['worker-creation-posts'] })
      navigate(withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: WorkerFormData) => {
    createMutation.mutate({
      post_id: data.post_id,
      display_name: data.display_name,
      phone: data.phone || undefined,
      email: data.email || undefined,
      worker_type: data.worker_type,
      verified: data.verified,
      service_instance: serviceInstanceId || undefined,
      categories: data.categories ?? [],
    })
  }

  const applySelectedPost = (post: WorkerCreationPost) => {
    if (post.has_worker_profile) {
      toast.error('A worker profile already exists for this post holder.')
      return
    }

    setSelectedPost(post)
    setPostPickerOpen(false)
    setValue('post_id', String(post.post_id || post.id), { shouldDirty: true, shouldValidate: true })

    if (!watch('display_name')?.trim() && post.user_name?.trim()) {
      setValue('display_name', post.user_name.trim(), { shouldDirty: true })
    }
  }

  const isVerified = watch('verified')

  const handleVerifiedChange = (checked: boolean) => {
    if (!checked) {
      setValue('verified', false, { shouldDirty: true, shouldValidate: true })
      setVerifiedConfirmOpen(false)
      return
    }

    setVerifiedConfirmOpen(true)
  }

  const confirmVerified = () => {
    setValue('verified', true, { shouldDirty: true, shouldValidate: true })
    setVerifiedConfirmOpen(false)
  }

  const cancelVerifiedConfirmation = () => {
    setValue('verified', false, { shouldDirty: true, shouldValidate: true })
    setVerifiedConfirmOpen(false)
    toast.error('Verification canceled. Worker remains unverified.')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(withServiceInstanceId('/workers', serviceInstanceId))}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Add Worker</h1>
          <p className="text-sm text-muted-foreground">Create a worker profile for an existing post holder in the current service branch.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Post holder</CardTitle>
              <CardDescription>Select an existing role assignment (post) in this branch. User and role are taken from the post automatically.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <input type="hidden" {...register('post_id')} />

              <div className="space-y-2">
                <Label htmlFor="post-search">Post holder *</Label>
                <Popover open={postPickerOpen} onOpenChange={setPostPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={postPickerOpen}
                      className={cn(
                        'h-auto min-h-10 w-full justify-between px-3 py-2 text-left font-normal',
                        !selectedPost && 'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {selectedPost
                          ? `${selectedPost.user_name || selectedPost.username || 'Post holder'} · ${selectedPost.role_name || 'Role'}`
                          : 'Search and select a post holder'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[420px] p-0">
                    <div className="border-b border-border p-3">
                      <Input
                        id="post-search"
                        placeholder="Search by holder name, username, or role"
                        value={postSearch}
                        onChange={(event) => setPostSearch(event.target.value)}
                      />
                    </div>
                    <div className="max-h-80 overflow-y-auto p-2">
                      {postsQuery.isLoading ? (
                        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                          <Spinner className="mr-2" />
                          Loading post holders...
                        </div>
                      ) : postsQuery.isError ? (
                        <p className="px-2 py-6 text-sm text-destructive">{getErrorMessage(postsQuery.error)}</p>
                      ) : !serviceInstanceId ? (
                        <p className="px-2 py-6 text-sm text-muted-foreground">Select a service instance context to load post holders.</p>
                      ) : filteredPosts.length > 0 ? (
                        filteredPosts.map((post) => {
                          const isSelected = String(selectedPost?.post_id || selectedPost?.id || '') === String(post.post_id || post.id)

                          return (
                            <button
                              key={String(post.id)}
                              type="button"
                              className="flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted"
                              onClick={() => applySelectedPost(post)}
                            >
                              <Check className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                              <div className="min-w-0">
                                <p className="truncate font-medium text-foreground">
                                  {post.user_name || post.username || 'Post holder'}
                                </p>
                                <p className="truncate text-sm text-muted-foreground">{post.role_name || 'Role'}</p>
                                {getPostSecondaryText(post) ? (
                                  <p className="truncate text-xs text-muted-foreground">{getPostSecondaryText(post)}</p>
                                ) : null}
                              </div>
                            </button>
                          )
                        })
                      ) : (
                        <p className="px-2 py-6 text-sm text-muted-foreground">
                          No eligible post holders found. Posts that already have a worker profile are excluded.
                        </p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {errors.post_id ? <p className="text-sm text-destructive">{errors.post_id.message}</p> : null}
                {selectedPost ? (
                  <div className="rounded-xl border border-border/70 bg-muted/20 p-3 text-sm">
                    <p className="font-medium text-foreground">{selectedPost.user_name || selectedPost.username}</p>
                    <p className="mt-1 text-muted-foreground">{selectedPost.role_name}</p>
                    {selectedPost.service_instance_name ? (
                      <p className="mt-1 text-muted-foreground">{selectedPost.service_instance_name}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">Display name *</Label>
                <Input id="display_name" placeholder="Visible worker name" {...register('display_name')} aria-invalid={!!errors.display_name} />
                {errors.display_name ? <p className="text-sm text-destructive">{errors.display_name.message}</p> : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="Optional override" {...register('email')} aria-invalid={!!errors.email} />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" placeholder="Optional override" {...register('phone')} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="worker_type">Worker type</Label>
                <Controller
                  control={control}
                  name="worker_type"
                  render={({ field }) => (
                    <Select value={String(field.value)} onValueChange={(value) => field.onChange(Number(value))}>
                      <SelectTrigger id="worker_type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Internal</SelectItem>
                        <SelectItem value="2">Contract</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="rounded-xl border border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
                <p>Current service branch</p>
                <p className="mt-1 font-medium text-foreground">{serviceInstanceId || 'Missing service branch context in URL'}</p>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Categories</CardTitle>
                <CardDescription>Optional job categories for worker matching on jobs.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Controller
                  control={control}
                  name="categories"
                  render={({ field }) => (
                    <div className="max-h-56 space-y-1 overflow-auto rounded-xl border border-border/70 p-3">
                      {categoriesQuery.isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Spinner className="h-4 w-4" />
                          Loading categories...
                        </div>
                      ) : categoriesQuery.isError ? (
                        <p className="text-sm text-destructive">{getErrorMessage(categoriesQuery.error)}</p>
                      ) : categories.length > 0 ? (
                        categories.map((cat) => {
                          const categoryId = normalizeCategoryId(cat.id)
                          const selectedIds = (field.value ?? []).map(normalizeCategoryId)
                          const isChecked = selectedIds.includes(categoryId)
                          return (
                            <label
                              key={categoryId}
                              htmlFor={`worker-create-category-${categoryId}`}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-muted/50"
                            >
                              <Checkbox
                                id={`worker-create-category-${categoryId}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  field.onChange(toggleCategory(field.value, categoryId, checked === true))
                                }}
                              />
                              <span className="text-sm text-foreground">{cat.name}</span>
                            </label>
                          )
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground">No categories available for this service branch.</p>
                      )}
                    </div>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">State</CardTitle>
                <CardDescription>Control worker approval and onboarding state.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center gap-3 rounded-xl border border-border/70 p-4">
                  <Checkbox checked={isVerified} onCheckedChange={(checked) => handleVerifiedChange(checked === true)} />
                  <div>
                    <p className="font-medium text-foreground">Verified</p>
                    <p className="text-sm text-muted-foreground">Mark the worker as verified if approval and onboarding are already complete.</p>
                  </div>
                </label>
              </CardContent>
            </Card>
          </div>
        </div>

        <Dialog open={verifiedConfirmOpen} onOpenChange={setVerifiedConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm verification</DialogTitle>
              <DialogDescription>
                This will mark the worker as verified. Use this only after approval and onboarding are complete.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={cancelVerifiedConfirmation}>
                Keep unverified
              </Button>
              <Button type="button" onClick={confirmVerified}>
                Mark verified
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(withServiceInstanceId('/workers', serviceInstanceId))}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending || !serviceInstanceId || !selectedPost}>
            {createMutation.isPending ? (
              <>
                <Spinner className="mr-2" />
                Creating...
              </>
            ) : 'Create Worker'}
          </Button>
        </div>
      </form>
    </div>
  )
}
